import React, { useState } from "react"
import {
	ReactComponent as ChatBotIcon
} from "@/assets/icons/chat_bot.svg"
import { Button, CircularProgress, Grid, Tooltip, Typography } from "@material-ui/core"

import clsx from "clsx"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"
import FlowListSelection from "@/components/ContactAction/FlowListSelection"
import ContactGroupPermissionGate from "@/components/PermissionGate/ContactGroupPermissionGate"
import useCustomStyles from "@/styles/custom"
import useStyles from "@/components/ContactAction/styles"
import ActionDialog from "@/components/ActionDialog"
import { ChatBotFlowInChatBotTriggerFormattedForList } from "@/services/ChatBotFlow"
import { handleGetSingleContactsFilterCount } from "@/utils/contact"
import ApiService from "@/services/Api"
import { useGlobalStateStore } from "@/store/GlobalState"
import SendToBotDialog from "@/components/ContactAction/SendToBotDialog"
import WarningImage from "@/assets/images/warning-bot.svg"
import { colors } from "@/styles/colors"
import { BaseContactsFiltersType } from "@/protocols/ClientCatalogFilterProtocol"
import { Notification } from "@/components"

/**
 * Must match "MAX_CONTACTS_TO_BULK_ADD_ON_BOT" in the "inbox-api".
 * The API remains the source of truth, this value only avoids a useless request.
 */
const MAX_CONTACTS_TO_ADD_ON_BOT = 2000
const MAX_CONTACTS_EXCEEDED_CODE_MESSAGE = "max_contacts_to_bulk_add_on_bot_exceeded"

const getMaxContactsExceededMessage = (contactsCount: number): string => {
	const formattedMaxContacts = MAX_CONTACTS_TO_ADD_ON_BOT.toLocaleString("pt-BR")

	/**
	 * The count shown here comes from another endpoint than the one that blocked the action,
	 * so it may not exceed the limit near the boundary. Only show it when it makes sense.
	 */
	if (contactsCount > MAX_CONTACTS_TO_ADD_ON_BOT) {
		return `O filtro selecionado possui ${contactsCount.toLocaleString("pt-BR")} contatos, mas só é possível adicionar até ${formattedMaxContacts} contatos ao bot por vez. Refine o filtro e tente novamente.`
	}

	return `O filtro selecionado possui mais contatos do que o permitido. É possível adicionar até ${formattedMaxContacts} contatos ao bot por vez. Refine o filtro e tente novamente.`
}

type PossibleUserTexts = {
	selectSendToBot: string
	sendToBot: string
	sendToBotButton: string
}

type ContactActionProps = {
	contactFilters?: BaseContactsFiltersType
	hasContactsOnCurrentFilter?: boolean
	contactAction: "single-contact" | "filter"
	client?: {
		clientId: number
		contactId?: number
		name?: string
	}
	clientDisplayName?: string
	hideOpenButton?: boolean
	open?: boolean
	setOpen?: React.Dispatch<React.SetStateAction<boolean>>
}

const ContactAction: React.FC<ContactActionProps> = (props) => {
	const {
		contactFilters,
		hasContactsOnCurrentFilter,
		contactAction,
		client,
		clientDisplayName,
		hideOpenButton,
		open,
		setOpen
	} = props

	const [openDialog, setOpenDialog] = useState(false)
	const [openWarningDialog, setOpenWarningDialog] = useState(false)
	const [loading, setLoading] = useState(false)
	const [selectedFlow, setSelectedFlow] = useState<ChatBotFlowInChatBotTriggerFormattedForList>()
	const [clientTexts, setClientTexts] = useState<PossibleUserTexts>()
	const [openSendToBotDialog, setOpenSendToBotDialog] = useState(false)

	const globalStateStore = useGlobalStateStore()
	const inboxChannelId = globalStateStore.currentChannel?.id

	const contactActionSubscriptionData = useSubscriptionLimits("permission", "bulk_contact_action")
	const isContactActionEnabled = !contactActionSubscriptionData.mustBlock
	const isContactSubscriptionLoading = contactActionSubscriptionData.isLoading
	const tooltipText = isContactActionEnabled ? "Para adicionar contatos a um bot, é necessário realizar um filtro." : "Essa opção está disponível apenas para o plano avançado."
	const canAddContactsOnBotByFilter = hasContactsOnCurrentFilter && contactFilters && contactAction === "filter"
	const isSingleContactAction = contactAction === "single-contact"
	const isContactActionButtonDisabled = isContactSubscriptionLoading || !isContactActionEnabled || !(canAddContactsOnBotByFilter || isSingleContactAction)

	const MAX_CONTACTS_WARNING = 200

	const customClasses = useCustomStyles()
	const classes = useStyles()

	const handleCloseDialog = async () => {
		setOpenDialog(false)
		openWarningDialog && setOpenWarningDialog(false)
		setOpen && setOpen(false)
	}

	const addContactsOnBotByFilter = async () => {
		await ApiService.post("/clients/bulk-add-contacts-on-bot", {
			contactFilters,
			chatBotFlowId: selectedFlow?.id,
			inboxChannelId
		})
	}

	const addSingleContactOnBot = async () => {
		await ApiService.post("/clients/add-contact-on-bot", {
			chatBotFlowId: selectedFlow?.id,
			inboxChannelId,
			client
		})
	}

	const getFilteredContactsCount = async () => {
		return contactFilters ? await handleGetSingleContactsFilterCount(contactFilters) : 0
	}

	/**
	 * Returns true when the error was the "too many contacts" validation and the user was already notified.
	 */
	const handleMaxContactsExceededError = async (error: unknown): Promise<boolean> => {
		const codeMessage = (error as { response?: { data?: { codeMessages?: { contact_filters?: string } } } })?.response?.data?.codeMessages?.contact_filters

		if (codeMessage !== MAX_CONTACTS_EXCEEDED_CODE_MESSAGE) {
			return false
		}

		const contactOnFilterCount = await getFilteredContactsCount()

		Notification.error({ message: getMaxContactsExceededMessage(contactOnFilterCount) })

		return true
	}

	const translateUserActionToReadbleText = async () => {
		let contactOnFilterCount = 0

		if (!isSingleContactAction && contactFilters) {
			contactOnFilterCount = await getFilteredContactsCount()
		}

		const formattedContactCountNumber = contactOnFilterCount < 10 ? contactOnFilterCount.toString().padStart(2, "0") : contactOnFilterCount

		const isAddingSingleContactOnFilter = !isSingleContactAction && contactOnFilterCount > 1

		const contactActionToClientTexts: Record<ContactActionProps["contactAction"], PossibleUserTexts> = {
			filter: {
				selectSendToBot: `Selecione para qual bot você quer enviar *${formattedContactCountNumber} contato${isAddingSingleContactOnFilter ? "s" : ""}!*`,
				sendToBot: isAddingSingleContactOnFilter ? `Os *${formattedContactCountNumber} contatos* foram enviados ao bot *${selectedFlow?.name}* com sucesso`
					: `*${formattedContactCountNumber} contato* foi enviado ao bot *${selectedFlow?.name}* com sucesso`,
				sendToBotButton: `Adicionar contato${isAddingSingleContactOnFilter ? "s" : ""} ao bot`
			},
			"single-contact": {
				selectSendToBot: "Selecione para qual bot você quer enviar o *contato!*",
				sendToBot: `O contato *${clientDisplayName ?? client?.name}* foi enviado ao bot *${selectedFlow?.name}* com sucesso`,
				sendToBotButton: "Adicionar contato ao bot"
			}
		}

		return contactActionToClientTexts[contactAction]
	}

	const handleOpenDialog = async () => {
		setLoading(true)
		setOpenDialog(true)
		setClientTexts(await translateUserActionToReadbleText())
		setLoading(false)
	}

	const handleSave = async () => {
		const contactOnFilterCount = await getFilteredContactsCount()

		if (contactAction === "filter" && contactOnFilterCount > MAX_CONTACTS_TO_ADD_ON_BOT) {
			Notification.error({ message: getMaxContactsExceededMessage(contactOnFilterCount) })
			handleCloseDialog()
			return
		}

		const hasTooManyContacts = contactOnFilterCount > MAX_CONTACTS_WARNING

		if (contactAction === "filter" && hasTooManyContacts) {
			setOpenWarningDialog(true)
			return
		}

		const contactActionToAddContactOnBot: Record<ContactActionProps["contactAction"], () => Promise<void>> = {
			filter: async () => await addContactsOnBotByFilter(),
			"single-contact": async () => await addSingleContactOnBot()
		}

		const sendToBotByContactAction = contactActionToAddContactOnBot[contactAction]

		setClientTexts(await translateUserActionToReadbleText())

		if (sendToBotByContactAction) {
			try {
				await sendToBotByContactAction()
			} catch (error) {
				const wasMaxContactsExceededError = await handleMaxContactsExceededError(error)

				if (!wasMaxContactsExceededError) {
					throw error
				}

				handleCloseDialog()
				return
			}
		}

		handleCloseDialog()
		setOpenSendToBotDialog(true)
	}

	const handleSaveNextWarning = async () => {
		setClientTexts(await translateUserActionToReadbleText())

		try {
			await addContactsOnBotByFilter()
		} catch (error) {
			const wasMaxContactsExceededError = await handleMaxContactsExceededError(error)

			if (!wasMaxContactsExceededError) {
				throw error
			}

			handleCloseDialog()
			return
		}

		handleCloseDialog()
		setOpenSendToBotDialog(true)
	}

	return (
		<>
			{!hideOpenButton && (
				<Tooltip
					disableHoverListener={Boolean(canAddContactsOnBotByFilter) || isSingleContactAction}
					title={tooltipText}
				>
					<span>
						<Grid item>
							<ContactGroupPermissionGate action="send-to-bot">
								<Button
									onClick={handleOpenDialog}
									startIcon={<ChatBotIcon />}
									disabled={isContactActionButtonDisabled}
									variant="contained"
									className={clsx(customClasses.secondaryActionButton, {
										[classes.singleContactPadding]: contactAction === "single-contact",
										[classes.multipleContactsPadding]: contactAction !== "single-contact"
									})}
									endIcon={isContactSubscriptionLoading && <CircularProgress size={16} />}
								>
									{contactAction === "single-contact" ? "Adicionar contato a um bot" : "Adicionar contatos a um bot"}
								</Button>
							</ContactGroupPermissionGate>
						</Grid>
					</span>
				</Tooltip>
			)}

			<ActionDialog
				openDialog={openDialog || open}
				loading={loading}
				title={"Adicionar ao bot"}
				saveText={clientTexts?.sendToBotButton}
				cancelText="Cancelar"
				saveButtonDisabled={Boolean(!selectedFlow?.id)}
				onClose={handleCloseDialog}
				onSave={handleSave}
				maxWidth="md"
				fullWidth
			>
				<FlowListSelection
					setSelectedFlow={setSelectedFlow}
					selectedFlow={selectedFlow}
					selectSendToBot={clientTexts?.selectSendToBot || ""}
				/>
			</ActionDialog>

			<ActionDialog
				openDialog={openWarningDialog}
				loading={loading}
				title={"Atenção"}
				saveText={"Continuar mesmo assim"}
				cancelText="Cancelar"
				onClose={handleCloseDialog}
				onSave={handleSaveNextWarning}
				maxWidth="sm"
				cancelButtonProps={{
					className: classes.validationDialogCancelButton,
					variant: "secondary"
				}}
				saveButtonProps={{
					className: classes.validationDialogSaveButton
				}}
				fullWidth
			>
				<Grid container justifyContent="center" alignItems="center" spacing={2} className={classes.warningContainer}>
					<img src={WarningImage} className={classes.picture} />

					<Grid item>
						<Typography>
							Para garantir o funcionamento ideal do bot, recomendamos selecionar até 200 contatos.
							Quantidades maiores podem afetar o desempenho, causando <span style={{ color: colors.red["500"] }}>lentidão</span> ou <span style={{ color: colors.red["500"] }}>travamentos</span>.
						</Typography>
					</Grid>
				</Grid>
			</ActionDialog >

			<SendToBotDialog
				open={openSendToBotDialog}
				setOpenSendToBotDialog={setOpenSendToBotDialog}
				sendToBotText={clientTexts?.sendToBot || ""} />
		</>
	)
}

export default ContactAction
